import moment from 'moment'
import React, { CSSProperties, ReactNode } from 'react'
import { images } from '../../../../images'
import { IAvailability, IPublicTicket, IStop } from '../../../../models'
import { LiteralsService } from '../../../../services'
import theme from '../../../../theme'
import StyledDiv from './styles'
import { TagComponent } from '../../../../components/ServiceCheckbox/TagComponent'
import { EventEndNotice, StopMarker, StopsRouteDots } from '../../../../components'
import { indicativeTimeMark } from '../../../../helpers/utils'

interface IProps {
  routeOption: IAvailability
  onClick: (ro: IAvailability) => void
  isLast: boolean
  pickedOriginBusStop?: IStop
  pickedDestinationBusStop?: IStop
  selected: boolean
  isSingleStop?: boolean
  activeTickets?: IPublicTicket[]
  onActiveTicketsClick?: (activeTickets: IPublicTicket[]) => void
}

export const Card: React.FC<IProps> = ({
  routeOption, onClick, isLast, pickedOriginBusStop, pickedDestinationBusStop, selected, isSingleStop,
  activeTickets = [], onActiveTicketsClick
}) => {
  const cardStyles = (): CSSProperties => {
    let styles = {}
    if (selected) styles = {
      border: `2px solid ${theme.colors.primary}` 
    }
    if (isLast) {
      styles = {
        ...styles,
        marginBottom: 24
      }
    }
    return {
      ...styles
    }
  }

  const timeText = (): string => {
    const timeMark = indicativeTimeMark(routeOption.returnAfterEventEndMinutes)
    const departureTime = moment(routeOption.inStop.dateTime).format('HH:mm') + timeMark
    const arrivalTime = moment(routeOption.outStops[0].dateTime).format('HH:mm') + timeMark

    // eslint-disable-next-line max-len
    return `${ LiteralsService.get('departure', true) } ${ departureTime } - ${ LiteralsService.get('arrival', true) } ${ arrivalTime }`
  }

  const isBeginingStopUnavailable = (): boolean => {
    const isBeginingStopDisabled = pickedOriginBusStop
      ? routeOption.inStop.name !== pickedOriginBusStop.name
      : false

    return routeOption.alternative && isBeginingStopDisabled
  }

  const isDestinationStopUnavailable = (): boolean => {
    const isDestinationStopDisabled = pickedDestinationBusStop
      ? routeOption.outStops[0].name !== pickedDestinationBusStop.name
      : false

    return routeOption.alternative && isDestinationStopDisabled
  }

  const routeIcons = (): ReactNode => {
    if (isBeginingStopUnavailable()) {
      return (
        <>
          <img src={ images['pedestrian'] } alt="pedestrian" />
          <span className="dotted">...</span>
        </>
      )
    }
    if (isDestinationStopUnavailable()) {
      return (
        <>
          <span className="dotted">...</span>
          <img src={ images['pedestrian'] } alt="pedestrian" />
        </>
      )
    }
    return (
      <>
        <img src={ images['pedestrian'] } alt="pedestrian" />
        <span className="dotted">...</span>
      </>
    )
  }

  const getStopTextWidth = () => {
    if (isSingleStop) return '100%'
    return isBeginingStopUnavailable() && !isDestinationStopUnavailable() ? '30%': '35%'
  }

  return (
    <StyledDiv className="card" onClick={ () => onClick(routeOption) } style={ cardStyles() }>
      <div className="details">
        <div className="row">
          <TagComponent tag={ routeOption.serviceInfo.tag } color={ routeOption.serviceInfo.color } clickable={false} />
          <span className='service-name'>{ routeOption.serviceInfo.name }</span>
          {
            routeOption.serviceInfo.alterationMessages?.length > 0 &&
            <img
              className='warning-icon'
              src={ images.alteration }
              alt={ LiteralsService.get('alterations.title', true) }
            />
          }
          {
            activeTickets.length > 0 &&
            <img
              className='warning-icon clickable'
              src={ images.activeTicket }
              alt={ LiteralsService.get('activeTickets.title', true) }
              onClick={ (event) => {
                event.stopPropagation()
                onActiveTicketsClick?.(activeTickets)
              } }
            />
          }
        </div>
        <div className="row">
          { isBeginingStopUnavailable() && routeIcons() }
          <img src={ images['bus'] }  alt="bus" />
          {
            (!isSingleStop || (isSingleStop && pickedOriginBusStop)) &&
            <div className="stop-name" style={{ maxWidth: getStopTextWidth() }}>
              {
                isSingleStop && 
                <StopMarker color={ theme.colors.origin } />
              }
              <span>
                { routeOption.inStop.name }
              </span>
            </div>
          }
          {
            !isSingleStop &&
            <div className="stops-dots">
              <StopsRouteDots 
                colorStart={ theme.colors.origin }
                colorEnd={ theme.colors.greenRadio }
                horizontal={ true }
              />
            </div>
          }
          {
            routeOption.outStops.length > 0 && 
            (!isSingleStop || (isSingleStop && pickedDestinationBusStop)) &&
            <div className="stop-name" style={{ maxWidth: getStopTextWidth() }}>
              {
                isSingleStop && 
                <StopMarker color={ theme.colors.greenRadio } />
              }
              <span>
                { routeOption.outStops[0].name }
              </span>
            </div>
          }
          { isDestinationStopUnavailable() && routeIcons() }
        </div>
        <div className="row" style={{ marginTop: '8px' }}>
          <span>{ timeText() }</span>
        </div>
        <EventEndNotice returnAfterEventEndMinutes={ routeOption.returnAfterEventEndMinutes } />
      </div>
    </StyledDiv>
  )
}
